"use client";

import { useCallback, useState } from "react";
import { ChevronLeft } from "lucide-react";
import SwiperComponent from "@/components/ui/swiper-component";
import Button from "@/components/ui/button";
import Title from "@/components/Title";
import AcademyCard from "./_card";
import AcademyCardHorizontal from "./_cardHorizontal";


/* چیدمان کارت افقی مطابق فیگما: دو ردیف، و در هر نما ۱.۵ کارت در موبایل،
   ۲ کارت از تبلت و ۳ کارت از دسکتاپ. در دسکتاپ شش کارت دقیقا یک صفحه می‌شوند،
   پس اسلایدر همان گرید قبلی دیده می‌شود و دکمه‌های ناوبری خودشان پنهان می‌مانند.
   `grid` باید در هر بریک‌پوینت هم تکرار شود، وگرنه سوایپر آن را از دست می‌دهد. */
const HORIZONTAL_ROWS = 2;

const HORIZONTAL_BREAKPOINTS = {
    768: { slidesPerView: 2, spaceBetween: 16, grid: { rows: HORIZONTAL_ROWS } },
    1024: { slidesPerView: 3, spaceBetween: 16, grid: { rows: HORIZONTAL_ROWS } },
};

export default function BlogCollection({
    collection_data,
    fields,
    storage_prefix_url,
}) {


    // بلاگ‌ها همراه ویجت از کوئری صفحه (getPrefixUrlDetails) می‌آیند؛ fetch کلاینتی نداریم
    const blogs = collection_data?.blogs || [];

    /* «مشاهده همه» فقط وقتی معنی دارد که همه‌ی کارت‌ها در یک نما جا نشوند.
       سوایپر همین را با `isLocked` (watchOverflow) می‌داند و در هر رسایز و
       بریک‌پوینت با رویدادهای lock/unlock خبر می‌دهد. */
    const [overflowing, setOverflowing] = useState(false);
    const syncOverflow = useCallback((swiper) => setOverflowing(!swiper?.isLocked), []);
    const handleLock = useCallback(() => setOverflowing(false), []);
    const handleUnlock = useCallback(() => setOverflowing(true), []);

    const horizontal = fields?.layout === "grid-horizontal-card";
    const limit = parseInt(fields?.limit) || 8;
    const items = blogs?.slice(0, limit) || [];

    const see_all_href = fields?.see_all_link?.url || fields?.see_all_link || "";

    if (!items.length) return null;

    const overflow_events = {
        onInit: syncOverflow,
        onLock: handleLock,
        onUnlock: handleUnlock,
    };

    return (
        <section className="container">

            <div className="mb-4 flex min-h-8 items-center justify-between gap-2 lg:mb-6">
                {fields?.section_title?.title && (
                    <div className="flex items-center gap-2">
                        {fields?.icon && (
                            <span
                                aria-hidden="true"
                                className="block shrink-0 text-jmt-main [&>svg]:size-5 lg:[&>svg]:size-[22px]"
                                dangerouslySetInnerHTML={{ __html: fields.icon }}
                            />
                        )}

                        <Title
                            type={fields?.section_title?.type}
                            className="text-base font-bold text-jmt-main lg:text-lg">
                            {fields?.section_title?.title}
                        </Title>
                    </div>
                )}

                {see_all_href && overflowing && (
                    <Button
                        href={see_all_href}
                        text={fields?.see_all_text || "مشاهده همه"}
                        end_icon={<ChevronLeft size={16} />}
                        color_type="tertiary"
                    />
                )}
            </div>

            {horizontal ? (
                <SwiperComponent
                    slides_shadow_space
                    slides={items.map((blog) => (
                        <AcademyCardHorizontal
                            key={blog?.id || blog?.seo?.full_url}
                            blog={blog}
                            storage_prefix_url={storage_prefix_url}
                        />
                    ))}
                    grid={{ rows: HORIZONTAL_ROWS, fill: "row" }}
                    breakpoints={HORIZONTAL_BREAKPOINTS}
                    slidesPerView={1.1}
                    spaceBetween={12}
                    grabCursor
                    navPosition="sides"
                    hideNavOnMobile
                    {...overflow_events}
                />
            ) : (
                <SwiperComponent
                    slides_shadow_space
                    slides={items.map((blog) => (
                        <AcademyCard
                            key={blog?.id || blog?.seo?.full_url}
                            blog={blog}
                            storage_prefix_url={storage_prefix_url}
                        />
                    ))}
                    breakpoints={{
                        480: { slidesPerView: 2.2, spaceBetween: 12 },
                        768: { slidesPerView: 3, spaceBetween: 16 },
                        1024: { slidesPerView: 4, spaceBetween: 16 },
                    }}
                    slidesPerView={1.5}
                    spaceBetween={12}
                    grabCursor
                    navPosition="sides"
                    hideNavOnMobile
                    {...overflow_events}
                />
            )}
        </section>
    );
}
